"use client";

import { cn } from "@guru/shared/utils/cn";
import type { ReactNode } from "react";
import { BeforeAfterCompare } from "@/components/marketing/before-after-compare";
import { CountUp } from "@/components/marketing/count-up";
import { FinanceTree } from "@/components/marketing/finance-tree";
import { PERSONA_CONTENT, type PersonaContent } from "@/components/marketing/persona/persona-data";
import { usePersonaStore } from "@/components/marketing/persona/persona-store";
import { Section } from "@/components/marketing/section";
import {
  SheetFrame,
  type SheetRow,
  SheetTable,
  StickyNote,
} from "@/components/marketing/spreadsheet";
import { TrialCTA } from "@/components/marketing/trial-cta";
import { ChatBubble } from "@/components/marketing/whatsapp-bits";
import { formatINR } from "@/lib/marketing/format";

/* ─────────────────────────── Shared panel shells ────────────────────────── */

/** Full-bleed "after" panel — the compare card itself is the frame. */
function AfterPanel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn("flex h-full items-center justify-center p-5 sm:p-7", className ?? "bg-card")}
    >
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}

/* ───────────────────────────── Day 1 · Invoice day ──────────────────────── */

function InvoiceBefore({ content }: { content: PersonaContent }) {
  const fee = formatINR(content.monthlyFee);
  const total = formatINR(content.monthlyFee * 42);
  const rows: SheetRow[] = [
    {
      id: "r1",
      cells: [
        { text: content.studentName },
        { text: fee, align: "right" },
        { text: "" },
        { text: "" },
      ],
    },
    {
      id: "r2",
      cells: [
        { text: "Meera J." },
        { text: fee, align: "right" },
        { text: "" },
        { text: "✓?", tone: "due", align: "center" },
      ],
    },
    {
      id: "r3",
      cells: [
        { text: "Arjun T." },
        { text: fee, align: "right" },
        { text: "½?", tone: "due", align: "center" },
        { text: "" },
      ],
    },
    {
      id: "r4",
      cells: [{ text: "Dev P." }, { text: fee, align: "right" }, { text: "" }, { text: "" }],
    },
    {
      id: "r5",
      cells: [
        { text: "Sara K." },
        { text: fee, align: "right" },
        { text: "" },
        { text: "✓?", tone: "due", align: "center" },
      ],
    },
    {
      id: "r6",
      cells: [
        { text: "+ 37 more…", tone: "muted" },
        { text: "…", tone: "muted", align: "right" },
        { text: "", tone: "muted" },
        { text: "", tone: "muted" },
      ],
    },
    {
      id: "tot",
      cells: [
        { text: "Total", tone: "highlight" },
        { text: total, tone: "highlight", align: "right" },
        { text: "", tone: "highlight" },
        { text: "", tone: "highlight" },
      ],
    },
  ];
  return (
    <SheetFrame cellRef="B44" formula="=SUM(B2:B43)" tabs={["Fees · June", "Pro-rata"]}>
      <div className="relative">
        <SheetTable
          columns={["A · Student", "B · Fee", "C · Joiner?", "D · Carry fwd"]}
          colTemplate="1.5fr .9fr .8fr .9fr"
          rows={rows}
        />
        <StickyNote className="bottom-1 left-1 -rotate-2">retype 42 rows again 😣</StickyNote>
      </div>
    </SheetFrame>
  );
}

function InvoiceAfter({ content }: { content: PersonaContent }) {
  const thisMonth = formatINR(content.monthlyFee * 42);
  const catchUps = formatINR(Math.round((content.monthlyFee * 2 * 17) / 30 / 10) * 10);
  const carried = formatINR(content.monthlyFee * 3);
  return (
    <AfterPanel>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Auto-invoice run · 1st of the month
        </p>
        <span className="rounded-full bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand-deep">
          42 invoices
        </span>
      </div>
      <div className="mt-3 space-y-2">
        {[
          { label: "This month's fees · 42 students", value: thisMonth },
          { label: "Pro-rata catch-ups · 2 joiners", value: `+ ${catchUps}` },
          { label: "Unpaid balances carried forward", value: `+ ${carried}` },
          { label: "Typed by you", value: "0" },
        ].map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2.5"
          >
            <span className="text-sm text-muted-foreground">{row.label}</span>
            <span className="shrink-0 text-sm font-bold tabular-nums text-foreground">
              {row.value}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-center text-xs text-muted-foreground">
        Optional: WhatsApp notification with a payment link, per invoice
      </p>
    </AfterPanel>
  );
}

/* ────────────────────────────── Day 5 · The chase ───────────────────────── */

function ChaseBefore({ content }: { content: PersonaContent }) {
  const fee = formatINR(content.monthlyFee);
  const rows: SheetRow[] = [
    {
      id: "c1",
      cells: [
        { text: content.parentName },
        { text: fee, align: "right" },
        { text: "Y", align: "center" },
      ],
    },
    {
      id: "c2",
      cells: [
        { text: "Mr Khan" },
        { text: fee, align: "right", tone: "due" },
        { text: "N", tone: "due", align: "center" },
      ],
    },
    {
      id: "c3",
      cells: [{ text: "Mrs Iyer" }, { text: fee, align: "right" }, { text: "Y", align: "center" }],
    },
    {
      id: "c4",
      cells: [
        { text: "Mr Thomas" },
        { text: fee, align: "right", tone: "due" },
        { text: "N", tone: "due", align: "center" },
      ],
    },
    {
      id: "c5",
      cells: [
        { text: "Mrs Reddy" },
        { text: fee, align: "right", tone: "due" },
        { text: "N", tone: "due", align: "center" },
      ],
    },
    {
      id: "c6",
      cells: [
        { text: "Mr Joseph" },
        { text: fee, align: "right", tone: "due" },
        { text: "N", tone: "due", align: "center" },
      ],
    },
    {
      id: "c7",
      cells: [
        { text: "+ 36 more…", tone: "muted" },
        { text: "…", tone: "muted", align: "right" },
        { text: "", tone: "muted" },
      ],
    },
  ];
  return (
    <SheetFrame cellRef="C2" formula={'=IF(C2="N","CHASE","")'} tabs={["Fees · June"]}>
      <div className="relative">
        <div className="mb-1.5 inline-flex rounded bg-background px-2 py-0.5 text-[10px] text-muted-foreground">
          Filter: Paid? = N
        </div>
        <SheetTable
          columns={["A · Parent", "B · Fee", "C · Paid?"]}
          colTemplate="1.4fr 1fr .7fr"
          rows={rows}
        />
        <StickyNote className="bottom-1 left-1 rotate-2">text these 4 by hand 😩</StickyNote>
      </div>
    </SheetFrame>
  );
}

function ChaseAfter({ content }: { content: PersonaContent }) {
  const fee = formatINR(content.monthlyFee);
  return (
    <AfterPanel className="bg-[#efeae2] dark:bg-[#0b141a]">
      <p className="mb-3 text-center text-xs font-medium text-[#667781] dark:text-[#8696a0]">
        Day 5 → due date — automatic follow-ups
      </p>
      <div className="space-y-2.5">
        <div>
          <p className="mb-0.5 pr-1 text-right text-[10px] text-[#667781] dark:text-[#8696a0]">
            to {content.parentName}
          </p>
          <ChatBubble direction="out" ticks="read">
            <span className="block">
              Fee reminder — {content.studentName} · <strong>{fee}</strong> due Friday
            </span>
            <span className="mt-1 block text-[12px] font-semibold text-[#008069] dark:text-[#00a884]">
              🔗 Pay securely
            </span>
          </ChatBubble>
        </div>
        <div className="flex justify-center">
          <span className="rounded-md bg-white/90 px-2.5 py-1 text-center text-[10px] font-medium text-[#54656f] shadow-sm dark:bg-[#182229] dark:text-[#8696a0]">
            {content.parentName} paid {fee} via the link — invoice marked paid
          </span>
        </div>
        <ChatBubble direction="out" ticks="delivered">
          <span className="block">
            Payment received ✅ — <strong>{fee}</strong> for {content.studentName} — thank you!
          </span>
        </ChatBubble>
      </div>
      <p className="mt-3 text-center text-[10px] text-[#667781] dark:text-[#8696a0]">
        No gateway connected? Mark it paid in a tap — same flow.
      </p>
    </AfterPanel>
  );
}

/* ──────────────────────────── Day 14 · A new joiner ─────────────────────── */

function JoinerBefore({ content }: { content: PersonaContent }) {
  const fee = formatINR(content.monthlyFee);
  const proRata = formatINR(Math.round((content.monthlyFee * 17) / 30 / 10) * 10);
  // A panicked miscount — 14 days instead of 17 — to contrast with the cell.
  const wrongGuess = formatINR(Math.round((content.monthlyFee * 14) / 30 / 10) * 10);
  const rows: SheetRow[] = [
    {
      id: "j1",
      cells: [
        { text: "Arjun T." },
        { text: "full", align: "center" },
        { text: fee, align: "right" },
      ],
    },
    {
      id: "j2",
      cells: [
        { text: "Sara K." },
        { text: "full", align: "center" },
        { text: fee, align: "right" },
      ],
    },
    {
      id: "jnew",
      cells: [
        { text: `${content.studentName} ⟵ new`, tone: "insert" },
        { text: "17 days", tone: "insert", align: "center" },
        { text: `${proRata}?`, tone: "insert", align: "right" },
      ],
    },
    {
      id: "j3",
      cells: [{ text: "Dev P." }, { text: "full", align: "center" }, { text: fee, align: "right" }],
    },
    {
      id: "j4",
      cells: [
        { text: "+ 39 more…", tone: "muted" },
        { text: "", tone: "muted", align: "center" },
        { text: "…", tone: "muted", align: "right" },
      ],
    },
  ];
  return (
    <SheetFrame
      cellRef="C4"
      formula={`=${content.monthlyFee}*17/30`}
      tabs={["Fees · June", "Extra invoice"]}
    >
      <div className="relative">
        <SheetTable
          columns={["A · Student", "B · Days", "C · Pro-rata"]}
          colTemplate="1.5fr .8fr 1fr"
          rows={rows}
        />
        <StickyNote className="bottom-1 left-1 -rotate-2">
          or {wrongGuess}? 🤔 separate bill
        </StickyNote>
      </div>
    </SheetFrame>
  );
}

function JoinerAfter({ content }: { content: PersonaContent }) {
  const proRata = formatINR(Math.round((content.monthlyFee * 17) / 30 / 10) * 10);
  return (
    <AfterPanel>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            New enrolment · 14th
          </p>
          <span className="rounded-full bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand-deep">
            Mid-cycle
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{content.studentName}</p>
            <p className="truncate text-xs text-muted-foreground">{content.batchName}</p>
          </div>
          <span className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
            Parent linked
          </span>
        </div>
        <div className="rounded-lg border border-border bg-background p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">Pro-rata catch-up · 14th–30th</span>
            <span className="text-sm font-bold tabular-nums text-foreground">{proRata}</span>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Worked out instantly — folds into next month's invoice
          </p>
        </div>
        <div className="rounded-lg border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
          Nothing sent to {content.parentName} today — one consolidated invoice on the 1st
        </div>
      </div>
    </AfterPanel>
  );
}

/* ──────────────────────────── Day 30 · Month-end ────────────────────────── */

function MonthEndBefore({ content }: { content: PersonaContent }) {
  const inr = (value: number) => formatINR(Math.round(value));
  const fee = content.monthlyFee;
  const rows: SheetRow[] = [
    { id: "m1", cells: [{ text: "Invoiced · June" }, { text: inr(fee * 120), align: "right" }] },
    { id: "m2", cells: [{ text: "Collected (count Y)" }, { text: "38", align: "right" }] },
    {
      id: "m3",
      cells: [{ text: "Still due (count N)" }, { text: "4", align: "right", tone: "due" }],
    },
    { id: "m4", cells: [{ text: "Main Road branch" }, { text: inr(fee * 70), align: "right" }] },
    { id: "m5", cells: [{ text: "Lakeside branch" }, { text: inr(fee * 50), align: "right" }] },
    { id: "m6", cells: [{ text: "Late fees?" }, { text: "by hand", align: "right", tone: "due" }] },
    {
      id: "pct",
      cells: [
        { text: "% collected", tone: "highlight" },
        { text: "92%?", tone: "due", align: "right" },
      ],
    },
  ];
  return (
    <SheetFrame cellRef="B7" formula={'=COUNTIF(D2:D43,"Y")/42'} tabs={["June", "May", "Apr"]}>
      <div className="relative">
        <SheetTable columns={["A · Metric", "B · Value"]} colTemplate="1.4fr 1fr" rows={rows} />
        <StickyNote className="bottom-1 left-1 rotate-2">recount tonight…</StickyNote>
      </div>
    </SheetFrame>
  );
}

function MonthEndAfter({ content }: { content: PersonaContent }) {
  const inr = (value: number) => formatINR(Math.round(value));
  const fee = content.monthlyFee;
  // Healthy, aspirational month — mirrors the product's Finance tab structure
  // (one ledger tree + a parent-less Adjustments tree), not literal test data.
  const totalInvoiced = fee * 120;
  const paid = Math.round(totalInvoiced * 0.92);
  const overdue = Math.round(totalInvoiced * 0.04);
  const pending = Math.round(totalInvoiced * 0.02);
  const partially = Math.round(totalInvoiced * 0.02);
  const lateFee = Math.round((fee * 0.6) / 10) * 10;
  const creditApplied = Math.round((fee * 1.2) / 50) * 50;

  return (
    <AfterPanel>
      <div className="space-y-4">
        <FinanceTree
          root={{
            label: "Total invoiced · this month",
            value: <CountUp value={totalInvoiced} format={inr} />,
            chip: { tone: "positive", text: "+12%", direction: "up" },
            subtitle: "92% collection rate",
            subtitleChip: { tone: "positive", text: "+4 pts", direction: "up" },
          }}
          leaves={[
            {
              label: "Paid",
              value: inr(paid),
              chip: { tone: "positive", text: "+15%", direction: "up" },
              drill: true,
            },
            {
              label: "Pending",
              value: inr(pending),
              chip: { tone: "neutral", text: "flat", direction: "flat" },
              drill: true,
            },
            {
              label: "Overdue",
              value: inr(overdue),
              chip: { tone: "positive", text: "-9%", direction: "down", note: "good" },
              drill: true,
            },
            {
              label: "Partially",
              value: inr(partially),
              chip: { tone: "neutral", text: "flat", direction: "flat" },
              drill: true,
            },
          ]}
        />
        <div className="border-t border-border pt-3">
          <FinanceTree
            heading={{ label: "Adjustments", caption: "Independent of the invoice ledger" }}
            leaves={[
              {
                label: "Late fee charged",
                value: inr(lateFee),
                chip: { tone: "positive", text: "-20%", direction: "down", note: "good" },
              },
              {
                label: "Credit held",
                value: inr(0),
                chip: { tone: "neutral", text: "flat", direction: "flat" },
              },
              {
                label: "Credit applied",
                value: inr(creditApplied),
                chip: { tone: "new", text: "new" },
              },
            ]}
          />
        </div>
      </div>
    </AfterPanel>
  );
}

/* ─────────────────────────────── The narrative ──────────────────────────── */

interface MonthBeat {
  day: number;
  sceneLabel: string;
  title: string;
  body: string;
  beforeLabel: string;
  afterLabel: string;
  ariaLabel: string;
  Before: (props: { content: PersonaContent }) => ReactNode;
  After: (props: { content: PersonaContent }) => ReactNode;
}

const BEATS: MonthBeat[] = [
  {
    day: 1,
    sceneLabel: "Invoice day",
    title: "Invoice day runs itself",
    body: "One consolidated invoice per student — catch-ups and carry-forwards already folded in. Drag to see the difference.",
    beforeLabel: "Before — spreadsheet",
    afterLabel: "After — EtheraGuru",
    ariaLabel: "Compare invoice day in a spreadsheet with EtheraGuru's automatic invoice run",
    Before: InvoiceBefore,
    After: InvoiceAfter,
  },
  {
    day: 5,
    sceneLabel: "The chase",
    title: "Follow-ups run themselves too",
    body: "Daily reminders and overdue nudges with a payment link — captured and marked paid automatically. No texting parents one by one.",
    beforeLabel: "Before — spreadsheet",
    afterLabel: "After — EtheraGuru",
    ariaLabel: "Compare chasing fees from a spreadsheet with EtheraGuru's automatic reminders",
    Before: ChaseBefore,
    After: ChaseAfter,
  },
  {
    day: 14,
    sceneLabel: "A new joiner",
    title: "A student joins mid-month — no maths, no extra invoice",
    body: "The pro-rata catch-up is worked out instantly and folded into next month's invoice. Nothing extra lands in the parent's chat today.",
    beforeLabel: "Before — spreadsheet",
    afterLabel: "After — EtheraGuru",
    ariaLabel:
      "Compare working out a mid-month joiner by hand with EtheraGuru's automatic pro-rata",
    Before: JoinerBefore,
    After: JoinerAfter,
  },
  {
    day: 30,
    sceneLabel: "Month-end",
    title: "Month-end, checked from the sofa",
    body: "Collection rate, revenue trends and overdue patterns across every batch and branch — no late-night tallying, no recounts.",
    beforeLabel: "Before — spreadsheet",
    afterLabel: "After — EtheraGuru",
    ariaLabel:
      "Compare a month-end tally in a spreadsheet with EtheraGuru's live finance dashboard",
    Before: MonthEndBefore,
    After: MonthEndAfter,
  },
];

function MonthBeatBlock({ beat, content }: { beat: MonthBeat; content: PersonaContent }) {
  const { Before, After } = beat;
  return (
    <div className="scroll-mt-28">
      <div className="mx-auto mb-5 max-w-2xl text-center">
        <p className="text-sm font-semibold uppercase tracking-widest text-brand-deep">
          Day {beat.day} · {beat.sceneLabel}
        </p>
        <h3 className="mt-2 text-balance text-xl font-semibold text-foreground sm:text-2xl">
          {beat.title}
        </h3>
        <p className="mx-auto mt-2 max-w-xl text-pretty text-muted-foreground">{beat.body}</p>
      </div>
      <div className="mx-auto max-w-3xl">
        <BeforeAfterCompare
          before={<Before content={content} />}
          after={<After content={content} />}
          beforeLabel={beat.beforeLabel}
          afterLabel={beat.afterLabel}
          ariaLabel={beat.ariaLabel}
        />
      </div>
    </div>
  );
}

/**
 * "A month with EtheraGuru" — a before/after story across one billing month
 * (invoice run → reminder and payment-link follow-ups → mid-month joiner →
 * month-end review). Each beat is a drag-to-reveal compare: the spreadsheet
 * you run today versus the same month running itself. Keeps the `#features`
 * anchor.
 */
export function MonthSection() {
  const persona = usePersonaStore((state) => state.persona);
  const content = PERSONA_CONTENT[persona];
  const place = content.orgName.toLowerCase().includes("centre") ? "your centre" : "your academy";

  return (
    <Section
      id="features"
      tone="tinted"
      eyebrow="A month with EtheraGuru"
      title="Run the month, not the spreadsheet"
      subtitle={`Drag each divider — the spreadsheet ${place} runs today on the left, the same month running itself on the right.`}
    >
      <div className="space-y-16 sm:space-y-24">
        {BEATS.map((beat) => (
          <MonthBeatBlock key={beat.day} beat={beat} content={content} />
        ))}
      </div>

      {/* Closing beat */}
      <div className="mt-16 flex flex-col items-center gap-5 text-center sm:mt-24">
        <p className="text-lg text-muted-foreground">
          <span className="font-mono text-sm text-brand-deep">Day 1, again</span> — catch-ups and
          carry-forwards fold in, and the next month starts itself. You taught.
        </p>
        <TrialCTA placement="month-narrative" label="Run next month on EtheraGuru" />
      </div>
    </Section>
  );
}
